function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { useMultipleSelection, useSelect } from 'downshift';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTestId } from '../../../../utils';
import { useDropdownMenuProps } from './useDropdownMenuProps';
import { getOptionPropsHelper } from './utils';
export var useMultiSelect = _ref => {
  var {
    ref,
    value,
    options: optionsList,
    onClick,
    onChange,
    onKeyDown
  } = _ref;
  var [options, setOptions] = useState(optionsList); // Makes sure we get the latest options list.

  useEffect(() => {
    setOptions(optionsList);
  }, [optionsList]);
  var isUncontrolled = value !== undefined && !onChange;
  useEffect(() => {
    if (isUncontrolled) {
      // eslint-disable-next-line no-console -- We need to log a warning if `value` is provided without `onChange`.
      console.error('Warning: You provided a `value` prop to a form field without an `onChange` handler. This will render a read-only field. Please set either `onChange` or `isReadOnly`.');
    }
  }, [isUncontrolled]);
  var defaultValue = useMemo(() => options.filter(option => value === null || value === void 0 ? void 0 : value.find(val => isEqual(val, option.value))), [options, value]);
  var [selectedOptions, setSelectedOptions] = useState(defaultValue);
  useEffect(() => {
    if (isUncontrolled) return;
    setSelectedOptions(defaultValue);
  }, [isUncontrolled, defaultValue]);

  var handleChange = newSelectedOptions => {
    if (isUncontrolled) return;
    setSelectedOptions(newSelectedOptions);
    var value = newSelectedOptions.map(option => option.value);
    onChange === null || onChange === void 0 ? void 0 : onChange({
      target: {
        value
      }
    });
  };

  var {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem
  } = useMultipleSelection({
    selectedItems: selectedOptions
  });
  var {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    setHighlightedIndex,
    getItemProps
  } = useSelect({
    defaultHighlightedIndex: 0,
    items: options,
    stateReducer: (state, _ref2) => {
      var {
        changes,
        type
      } = _ref2;

      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return _objectSpread(_objectSpread({}, changes), {}, {
            isOpen: true,
            // This prevents the `highlightedIndex` from being reset and keeps it on the current selected item.
            highlightedIndex: state.highlightedIndex
          });
      }

      return changes;
    },
    onStateChange: _ref3 => {
      var {
        type,
        selectedItem: newSelectedOption
      } = _ref3;

      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (newSelectedOption) {
            if (selectedOptions !== null && selectedOptions !== void 0 && selectedOptions.find(option => isEqual(option.value, newSelectedOption.value))) {
              removeSelectedItem(newSelectedOption);
              handleChange(selectedOptions.filter(option => !isEqual(option.value, newSelectedOption.value)));
            } else {
              addSelectedItem(newSelectedOption);
              handleChange([...selectedOptions, newSelectedOption]);
            }
          }

          break;

        default:
          break;
      }
    },
    // Disables single selection.
    selectedItem: null
  }); // Prevent sending the form when the menu is closed.
  // Based on https://github.com/downshift-js/downshift#customizing-handlers

  var handleKeyDown = event => {
    if (event.key === 'Enter') event.preventDefault();
    onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(event);
  };

  var {
    anchorRef,
    dropdownMenuProps
  } = useDropdownMenuProps({
    ref,
    isOpen,
    getMenuProps,
    resetHighlightedIndex: () => setHighlightedIndex(-1),
    options
  });
  return {
    dropdownMenuProps,
    toggleButtonProps: getToggleButtonProps(getDropdownProps({
      preventKeyAction: isOpen,
      ref: anchorRef,
      onClick,
      onKeyDown: handleKeyDown,
      // 'aria-labelledby' causes a11y issue because its value doesn't correspond to an element in the document.
      // The reason is because we use our own label (only when needed) using `FormField` wrapper instead of using `downshift`.
      'aria-labelledby': undefined
    })),
    getOptionProps: getOptionPropsHelper({
      getItemProps,
      highlightedIndex,
      getTestId: useTestId('base-select')
    }),
    selectedOptions
  };
};
//# sourceMappingURL=useMultiSelect.js.map